import { useEffect, useState } from "react";
import { Menu, X, BadgeCheck, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { SITE } from "../config/site";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navigation"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "glass-light shadow-[0_10px_30px_-18px_rgba(6,78,59,0.35)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-8 lg:px-16">
        <Logo />
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium text-[#1C2826]/80 transition-colors duration-300 hover:text-[#064E3B]"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={SITE.mantraCareUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-mantra-care-badge"
            className="flex items-center gap-1.5 rounded-full border border-[#064E3B]/15 bg-[#ECFDF5] px-3 py-1.5 text-xs font-semibold text-[#064E3B] transition-transform duration-300 hover:-translate-y-0.5"
          >
            <BadgeCheck size={14} /> Mantra Care Verified
          </a>
          <a
            href="#book"
            data-testid="nav-book-session-button"
            className="group flex items-center gap-1.5 rounded-full bg-[#064E3B] px-5 py-2.5 text-sm font-semibold text-[#FDFBF7] transition-all duration-300 hover:bg-[#022C22] hover:shadow-[0_10px_30px_-10px_rgba(6,78,59,0.5)]"
          >
            Book a Session
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#064E3B]/15 text-[#064E3B] lg:hidden"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="glass-light border-t border-[#064E3B]/10 px-6 py-5 lg:hidden">
          <nav className="flex flex-col gap-4" aria-label="Mobile">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="font-serif text-2xl text-[#064E3B]"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#book"
              onClick={() => setOpen(false)}
              data-testid="mobile-book-session-button"
              className="mt-2 flex w-fit items-center gap-2 rounded-full bg-[#064E3B] px-6 py-3 text-sm font-semibold text-[#FDFBF7]"
            >
              Book a Session <ArrowUpRight size={16} />
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
